import { useMemo, useState } from "react";
import { ExternalLink, Loader2 } from "lucide-react";
import type { HotmartAccessCase } from "../../services/chatbotV2/chatbotV2Service";
import { HOTMART_MEMBERS_IMPORT_URL, hotmartAccessCaseStatusLabel } from "../../utils/hotmartClubUtils";

const STATUS_OPTIONS = [
  { value: "open", label: "Abierto" },
  { value: "in_progress", label: "En progreso" },
  { value: "resolved", label: "Resuelto" },
  { value: "dismissed", label: "Descartado" },
];

export function HotmartAccessCasesPanel({
  cases,
  loading,
  actionLoadingId,
  onRefresh,
  onUpdateCase,
}: {
  cases: HotmartAccessCase[];
  loading: boolean;
  actionLoadingId: number | null;
  onRefresh: () => void;
  onUpdateCase: (
    accessCase: HotmartAccessCase,
    patch: Partial<Pick<HotmartAccessCase, "case_status" | "resolution_note">>
  ) => void;
}) {
  const [statusFilter, setStatusFilter] = useState<string>("open");
  const filteredCases = useMemo(() => {
    if (statusFilter === "all") return cases;
    return cases.filter((row) => row.case_status === statusFilter);
  }, [cases, statusFilter]);

  return (
    <section className="mt-8 space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h3 className="text-sm font-semibold text-gray-950">Casos de acceso pendientes</h3>
          <p className="mt-1 max-w-2xl text-sm text-gray-500">
            Cuando la IA detecta compra sin alumno en Club, aparece aquí. Importa al alumno en Hotmart y marca el caso
            como resuelto.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <select
            value={statusFilter}
            onChange={(event) => setStatusFilter(event.target.value)}
            className="h-9 rounded-full border border-gray-200 bg-white px-3 text-sm text-gray-800 outline-none"
          >
            <option value="open">Abiertos</option>
            <option value="in_progress">En progreso</option>
            <option value="resolved">Resueltos</option>
            <option value="all">Todos</option>
          </select>
          <button
            type="button"
            onClick={onRefresh}
            disabled={loading}
            className="inline-flex h-9 items-center gap-2 rounded-full bg-gray-100 px-3 text-sm font-semibold text-gray-800 hover:bg-gray-200 disabled:opacity-50"
          >
            {loading ? <Loader2 className="size-4 animate-spin" /> : null}
            Actualizar
          </button>
          <a
            href={HOTMART_MEMBERS_IMPORT_URL}
            target="_blank"
            rel="noreferrer"
            className="inline-flex h-9 items-center gap-2 rounded-full bg-gray-950 px-3 text-sm font-semibold text-white hover:bg-gray-800"
          >
            Importar en Hotmart
            <ExternalLink className="size-3.5" />
          </a>
        </div>
      </div>

      <div className="overflow-hidden rounded-xl border border-gray-200">
        <table className="w-full text-left text-sm">
          <thead className="bg-gray-50 text-xs uppercase text-gray-500">
            <tr>
              <th className="px-4 py-3">Alumno / producto</th>
              <th className="px-4 py-3">Estado</th>
              <th className="px-4 py-3">Notas</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 bg-white">
            {filteredCases.map((accessCase) => (
              <tr key={accessCase.id}>
                <td className="px-4 py-3 align-top">
                  <p className="font-semibold text-gray-950">{accessCase.customer_email}</p>
                  <p className="mt-1 text-xs text-gray-500">
                    {accessCase.product_name || accessCase.product_id || "Producto sin nombre"}
                  </p>
                  <p className="mt-1 text-xs text-gray-400">{accessCase.case_reason}</p>
                </td>
                <td className="px-4 py-3 align-top">
                  <select
                    value={accessCase.case_status}
                    disabled={actionLoadingId === accessCase.id}
                    onChange={(event) =>
                      onUpdateCase(accessCase, {
                        case_status: event.target.value,
                      })
                    }
                    className="h-9 w-full min-w-[140px] rounded-lg border border-gray-200 bg-white px-2 text-sm text-gray-800 outline-none"
                  >
                    {STATUS_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                  <p className="mt-1 text-xs text-gray-500">{hotmartAccessCaseStatusLabel(accessCase.case_status)}</p>
                </td>
                <td className="px-4 py-3 align-top">
                  <textarea
                    defaultValue={accessCase.resolution_note || ""}
                    rows={2}
                    placeholder="Nota interna de resolución"
                    disabled={actionLoadingId === accessCase.id}
                    onBlur={(event) => {
                      const next = event.target.value.trim();
                      if (next === (accessCase.resolution_note || "")) return;
                      onUpdateCase(accessCase, { resolution_note: next || null });
                    }}
                    className="w-full min-w-[220px] rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-800 outline-none transition focus:border-gray-400"
                  />
                </td>
              </tr>
            ))}
            {!loading && filteredCases.length === 0 ? (
              <tr>
                <td colSpan={3} className="px-4 py-10 text-center text-sm text-gray-500">
                  {statusFilter === "open"
                    ? "No hay casos abiertos. Cuando la IA encole activación manual, aparecerán aquí."
                    : "Sin casos para este filtro."}
                </td>
              </tr>
            ) : null}
            {loading ? (
              <tr>
                <td colSpan={3} className="px-4 py-10 text-center text-sm text-gray-500">
                  <Loader2 className="mx-auto size-5 animate-spin text-gray-400" />
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>
    </section>
  );
}
